/**
 * Page manager section watchers
 * @module services/pm-section/watchers
 */
import { Map, fromJS } from 'immutable'
import { getUserLanguagePrimary } from '../user/index'
import { TYPE_CONTENT_SUBCATEGORY } from '../content-type/index'
import { setJumbotronData } from '../jumbotron/actions'
import { STORE_KEY_SUBCATEGORY } from '../store-keys/index'
import { getPmScreenTypeFromResolver } from '../pm-screen/index'
import {
  GET_MULTIPLE_PM_SECTIONS,
  GET_PM_SECTION,
  SET_PM_SECTION,
  SET_MULTIPLE_PM_SECTIONS,
} from './actions'
import { TYPE_HEADER } from './index'
import { getPmSectionMultiple, getPmSectionSingle } from './watchers-helpers'

export function onGetMultiplePmSections ({ after }) {
  return after(GET_MULTIPLE_PM_SECTIONS, async ({ dispatch, state, action }) => {
    const {
      auth = Map(),
      resolver = Map(),
      pmSection = Map(),
      user = Map(),
    } = state
    const {
      payload: {
        sectionIds,
        limit,
        language = getUserLanguagePrimary(user),
      } = {},
    } = action

    await getPmSectionMultiple({
      dispatch,
      language,
      auth,
      resolver,
      pmSection,
      sectionIds,
      limit,
    })
  })
}

export function onGetPmSection ({ after }) {
  return after(GET_PM_SECTION, async ({ dispatch, state, action }) => {
    const {
      auth = Map(),
      pmSection = Map(),
      user = Map(),
      resolver = Map(),
    } = state
    const {
      payload: {
        sectionId,
        language = getUserLanguagePrimary(user),
      } = {},
    } = action

    await getPmSectionSingle({
      dispatch,
      language,
      auth,
      resolver,
      pmSection,
      sectionId,
    })
  })
}

/**
 * Set jumbotron store when header section is set for PageManager pages
 * This ensures SubcategoryDescriptionFull can read from jumbotron store
 * as it was originally designed, maintaining consistency with SubcategoryPage
 */
export function onPmSectionSetJumbotron ({ after }) {
  return after(
    [SET_PM_SECTION, SET_MULTIPLE_PM_SECTIONS],
    ({ dispatch, state, action }) => {
      const { resolver = Map(), auth = Map() } = state
      const pmScreenType = getPmScreenTypeFromResolver({ resolver, auth })

      if (!pmScreenType) {
        return
      }

      switch (action.type) {
        case SET_PM_SECTION: {
          const { payload: { data } = {} } = action
          const sectionData = Map.isMap(data) ? data : fromJS(data)
          const sectionType = sectionData.get('type')

          if (sectionType === TYPE_HEADER) {
            syncJumbotronFromHeaderSection(dispatch, sectionData)
          }
          break
        }
        case SET_MULTIPLE_PM_SECTIONS: {
          const { payload: { sections = [] } = {} } = action
          const headerSection = sections.find((section) => {
            const sectionData = Map.isMap(section) ? section : fromJS(section)
            const appData = sectionData.get('app') || sectionData
            const sectionType = appData.get('type') || appData.getIn(['data', 'type'])
            return sectionType === TYPE_HEADER
          })

          if (headerSection) {
            const sectionData = Map.isMap(headerSection)
              ? headerSection
              : fromJS(headerSection)
            const appData = sectionData.get('app') || sectionData
            syncJumbotronFromHeaderSection(
              dispatch,
              appData,
            )
          }
          break
        }
        default:
          break
      }
    },
  )
}

/**
 * Sync jumbotron store with header section data
 * @param {Function} dispatch Redux dispatch function
 * @param {Map} sectionData The header section data (app object from GraphQL)
 */
function syncJumbotronFromHeaderSection (
  dispatch,
  sectionData,
) {
  // Extract data from header section
  // sectionData is the 'app' object, so sectionData.data is the union type
  // (PMSectionAppDataHeaderSection)
  const headerData = sectionData.get('data', Map())
  const title = headerData.get('title', '')
  const description = headerData.get('description', '')
  const assetRenderUrl = headerData.getIn(['asset', 'renderUrl'])
  const imageUrl = assetRenderUrl || headerData.get('imageUrl', '')
  const sectionId = sectionData.get('id')

  if (!title && !description) {
    return
  }

  // Build jumbotron data structure matching what SubcategoryPage expects
  const jumbotronData = {
    id: sectionId || -1,
    type: {
      content: TYPE_CONTENT_SUBCATEGORY,
      product: null,
    },
    title,
    description,
    heroImage: {
      small: imageUrl,
      mediumSmall: imageUrl,
      medium: imageUrl,
      large: imageUrl,
    },
  }

  // Set jumbotron data in store
  dispatch(setJumbotronData(STORE_KEY_SUBCATEGORY, jumbotronData, false))
}
